/**
 * OCS Cart Chip Component
 *
 * Big pill in the header showing gram equivalency + bag icon.
 * Keeps header chip, drawer gram meter, drawer count, and subtotal synced from
 * server-rendered cart sections whenever the Ajax cart changes.
 */

/**
 * @param {string|number|undefined|null} value
 * @param {number} fallback
 * @returns {number}
 */
function parseNumber(value, fallback = 0) {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * @param {number|string|undefined|null} value
 * @returns {string}
 */
function formatGramValue(value) {
  const parsed = parseNumber(value, 0);
  const rounded = Math.round(parsed * 1000) / 1000;
  return String(rounded);
}

/**
 * @param {ParentNode} root
 * @returns {{ currentGrams?: string, maxGrams?: string, overLimit?: boolean, countText?: string, subtotalText?: string }|null}
 */
function getCartSnapshotFromRoot(root) {
  const gramMeter = root.querySelector('.ocs-gram-meter[data-current-grams]');
  const chip = root.querySelector('ocs-cart-chip-component[data-current-grams]');
  const gramSource = gramMeter || chip;

  const currentGrams = gramSource ? formatGramValue(/** @type {HTMLElement} */ (gramSource).dataset.currentGrams) : undefined;
  const maxGrams = gramSource ? formatGramValue(/** @type {HTMLElement} */ (gramSource).dataset.maxGrams || 30) : undefined;
  const overLimit = currentGrams !== undefined && maxGrams !== undefined
    ? parseNumber(currentGrams, 0) > parseNumber(maxGrams, 30)
    : undefined;

  const countText =
    root.querySelector('[ref="cartItemCount"]')?.textContent?.trim() ||
    root.querySelector('[data-ocs-header-count]')?.textContent?.trim() ||
    root.querySelector('[data-ocs-chip-count]')?.textContent?.trim() ||
    root.querySelector('.cart-bubble__text-count')?.textContent?.trim() ||
    undefined;

  const subtotalText =
    root.querySelector('[data-ocs-chip-subtotal]')?.textContent?.trim() ||
    root.querySelector('.ocs-cart-summary__subtotal .ocs-cart-summary__value')?.textContent?.trim() ||
    undefined;

  if (currentGrams === undefined && countText === undefined && subtotalText === undefined) return null;

  return {
    currentGrams,
    maxGrams,
    overLimit,
    countText,
    subtotalText,
  };
}

/**
 * @param {Record<string, string>|undefined} sections
 * @returns {{ currentGrams?: string, maxGrams?: string, overLimit?: boolean, countText?: string, subtotalText?: string }|null}
 */
function getCartSnapshotFromSections(sections) {
  if (!sections) return null;

  const parser = new DOMParser();
  /** @type {{ currentGrams?: string, maxGrams?: string, overLimit?: boolean, countText?: string, subtotalText?: string }} */
  const snapshot = {};

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const parsed = parser.parseFromString(html, 'text/html');
    const partial = getCartSnapshotFromRoot(parsed);
    if (!partial) continue;

    if (partial.currentGrams !== undefined) snapshot.currentGrams = partial.currentGrams;
    if (partial.maxGrams !== undefined) snapshot.maxGrams = partial.maxGrams;
    if (partial.overLimit !== undefined) snapshot.overLimit = partial.overLimit;
    if (partial.countText !== undefined) snapshot.countText = partial.countText;
    if (partial.subtotalText !== undefined) snapshot.subtotalText = partial.subtotalText;
  }

  return Object.keys(snapshot).length ? snapshot : null;
}

class OcsCartChipComponent extends HTMLElement {
  #pill = null;
  #currentEls = null;
  #countEl = null;
  #subtotalEl = null;
  #refreshToken = 0;

  connectedCallback() {
    this.#pill = this.querySelector('[data-ocs-chip-pill]');
    this.#currentEls = this.querySelectorAll('[data-ocs-chip-current]');
    this.#countEl = this.querySelector('[data-ocs-chip-count]');
    this.#subtotalEl = this.querySelector('[data-ocs-chip-subtotal]');

    // Click anywhere on the pill → open the cart drawer
    this.#pill?.addEventListener('click', this.#onPillClick);

    // Sync values whenever cart changes
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.#pill?.removeEventListener('click', this.#onPillClick);
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  #onPillClick = () => {
    /** @type {HTMLElement & { open?: () => void } | null} */
    const drawer = document.querySelector('ocs-cart-drawer-component');
    if (typeof drawer?.open === 'function') {
      drawer.open();
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const cartUrl = new URL(`${normalizedRoot}cart`, window.location.origin);
    if (window.location.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '')) {
      window.location.href = cartUrl.href;
    }
  };

  /**
   * Sync immediately from event section HTML, then reconcile against fresh
   * server state. Add-to-cart surfaces do not all return the same sections, so
   * DOM-only synchronization can leave the header grams and count stale.
   */
  #onCartUpdate = (event) => {
    const token = ++this.#refreshToken;
    const sectionSnapshot = getCartSnapshotFromSections(/** @type {any} */ (event).detail?.data?.sections);
    if (sectionSnapshot) this.#applySnapshot(sectionSnapshot);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#syncFromDOM());
    });

    this.#refreshFromServer(token);
  };

  /** Reconcile count, gram total, subtotal, and limit state from Liquid. */
  async #refreshFromServer(token) {
    const cartItems = document.querySelector('cart-items-component[data-section-id]');
    const sectionId = /** @type {HTMLElement|null} */ (cartItems)?.dataset.sectionId;

    try {
      if (sectionId) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
        url.searchParams.set('_ocs_cart_chip', String(Date.now()));
        const response = await fetch(url, {
          cache: 'no-store',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Section Rendering API returned ${response.status}`);

        const html = await response.text();
        if (token !== this.#refreshToken || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const snapshot = getCartSnapshotFromRoot(parsed);
        if (snapshot) this.#applySnapshot(snapshot);
        return;
      }

      // Non-drawer themes have no cart section to render. Cart JSON still gives
      // the authoritative item count, which is the only dynamic value their
      // compact icon exposes.
      const root = window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      const response = await fetch(`${normalizedRoot}cart.js`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart API returned ${response.status}`);

      const cart = await response.json();
      if (token !== this.#refreshToken || !this.isConnected) return;
      this.#applySnapshot({ countText: String(Number(cart.item_count) || 0) });
    } catch (error) {
      console.error('[OCS] Cart chip refresh failed:', error);
    }
  }

  /** Pull updated values from the morphed cart drawer DOM. */
  #syncFromDOM() {
    const snapshot = getCartSnapshotFromRoot(document);
    if (snapshot) this.#applySnapshot(snapshot);
  }

  /**
   * @param {{ currentGrams?: string, maxGrams?: string, overLimit?: boolean, countText?: string, subtotalText?: string }} snapshot
   */
  #applySnapshot(snapshot) {
    const currentGrams = snapshot.currentGrams ?? this.dataset.currentGrams ?? '0';
    const maxGrams = snapshot.maxGrams ?? this.dataset.maxGrams ?? '30';
    const overLimit = snapshot.overLimit ?? (parseNumber(currentGrams, 0) > parseNumber(maxGrams, 30));

    this.dataset.currentGrams = currentGrams;
    this.dataset.maxGrams = maxGrams;

    this.#currentEls?.forEach((el) => {
      el.textContent = currentGrams;
    });

    // Keep every visible gram meter aligned with the same server-rendered state.
    document.querySelectorAll('.ocs-gram-meter[data-current-grams]').forEach((meter) => {
      const meterEl = /** @type {HTMLElement} */ (meter);
      meterEl.dataset.currentGrams = currentGrams;
      meterEl.dataset.maxGrams = maxGrams;
      meterEl.dataset.overLimit = String(overLimit);
      meterEl.classList.toggle('ocs-gram-meter--exceeded', overLimit);

      const currentEl = meterEl.querySelector('.js-ocs-current-grams');
      if (currentEl) currentEl.textContent = `${currentGrams}g`;

      const maxEl = meterEl.querySelector('.ocs-gram-meter__max');
      if (maxEl) maxEl.textContent = `${maxGrams}g`;
    });

    this.classList.toggle('ocs-gram-pill-wrapper--exceeded', overLimit);
    this.#pill?.classList.toggle('ocs-gram-pill--exceeded', overLimit);

    if (snapshot.subtotalText && this.#subtotalEl) {
      this.#subtotalEl.textContent = snapshot.subtotalText;
    }

    if (snapshot.countText !== undefined) {
      if (this.#countEl) {
        this.#countEl.textContent = snapshot.countText;
        this.#countEl.classList.toggle('has-three-digits', parseNumber(snapshot.countText, 0) > 99);
      }
      document.querySelectorAll('[data-ocs-header-count]').forEach((el) => {
        el.textContent = snapshot.countText || '0';
      });
    }

    this.#syncCheckoutButtons(overLimit, snapshot.countText);
    this.#syncAriaLabel(snapshot.countText);
  }

  /**
   * @param {boolean} overLimit
   * @param {string|undefined} countText
   */
  #syncCheckoutButtons(overLimit, countText) {
    const itemCount = countText !== undefined ? parseNumber(countText, 0) : undefined;

    document.querySelectorAll('[data-ocs-role="checkout-button"]').forEach((button) => {
      const checkoutButton = /** @type {HTMLButtonElement} */ (button);
      checkoutButton.dataset.ocsOverLimit = String(overLimit);

      const scope = checkoutButton.closest('ocs-cart-drawer-component, ocs-cart-page-component, .ocs-cart-summary');
      const termsCheckbox = /** @type {HTMLInputElement|null} */ (
        scope?.querySelector('[data-ocs-role="terms-checkbox"]') || document.querySelector('[data-ocs-role="terms-checkbox"]')
      );
      const isCartEmpty = itemCount !== undefined ? itemCount === 0 : !document.querySelector('.ocs-cart-item');
      checkoutButton.disabled = !termsCheckbox?.checked || overLimit || isCartEmpty;
    });
  }

  /**
   * @param {string|undefined} countText
   */
  #syncAriaLabel(countText) {
    const template = this.#pill?.dataset.labelTemplate;
    if (!template || !this.#pill) return;

    const label = template
      .replace('{grams}', this.dataset.currentGrams || '0')
      .replace('{max}', this.dataset.maxGrams || '30')
      .replace('{count}', countText ?? this.#countEl?.textContent?.trim() ?? '0');
    this.#pill.setAttribute('aria-label', label);
  }
}

if (!customElements.get('ocs-cart-chip-component')) {
  customElements.define('ocs-cart-chip-component', OcsCartChipComponent);
}
